import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, StickyNote, CalendarPlus, ImageIcon, Check, X } from "lucide-react";
import { addNote, deleteNote, addMeeting, deleteMeeting, setMoodBoardStatus } from "@/lib/apiClient";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

/**
 * CRM panel for an application: notes, meeting log, mood board reviews.
 * Local state is optimistically kept in sync with mutations so the admin
 * doesn't have to reload after every action.
 */
export default function AdminCrm({ application, onImageClick, onChange }) {
  const [notes, setNotes] = useState(application.admin_notes || []);
  const [meetings, setMeetings] = useState(application.meetings || []);
  const [moodBoards, setMoodBoards] = useState(application.mood_boards || []);
  const [noteDraft, setNoteDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [showMeetingForm, setShowMeetingForm] = useState(false);

  useEffect(() => {
    setNotes(application.admin_notes || []);
    setMeetings(application.meetings || []);
    setMoodBoards(application.mood_boards || []);
  }, [application]);

  const commitChange = (patch) => {
    onChange?.({ ...application, ...patch });
  };

  const submitNote = async () => {
    const text = noteDraft.trim();
    if (!text) return;
    setBusy(true);
    try {
      const entry = await addNote(application.id, text);
      const updated = [...notes, entry];
      setNotes(updated);
      commitChange({ admin_notes: updated });
      setNoteDraft("");
    } catch {
      toast.error("Could not save note.");
    } finally {
      setBusy(false);
    }
  };

  const removeNote = async (id) => {
    try {
      await deleteNote(application.id, id);
      const updated = notes.filter((n) => n.id !== id);
      setNotes(updated);
      commitChange({ admin_notes: updated });
    } catch {
      toast.error("Could not delete note.");
    }
  };

  const submitMeeting = async (payload) => {
    setBusy(true);
    try {
      const entry = await addMeeting(application.id, payload);
      const updated = [...meetings, entry];
      setMeetings(updated);
      commitChange({ meetings: updated });
      setShowMeetingForm(false);
    } catch {
      toast.error("Could not log meeting.");
    } finally {
      setBusy(false);
    }
  };

  const removeMeeting = async (id) => {
    try {
      await deleteMeeting(application.id, id);
      const updated = meetings.filter((m) => m.id !== id);
      setMeetings(updated);
      commitChange({ meetings: updated });
    } catch {
      toast.error("Could not delete meeting.");
    }
  };

  const decideMoodBoard = async (mbId, status) => {
    try {
      await setMoodBoardStatus(application.id, mbId, status);
      const updated = moodBoards.map((mb) => (mb.id === mbId ? { ...mb, status } : mb));
      setMoodBoards(updated);
      commitChange({ mood_boards: updated });
      toast.success(`Mood board ${status}.`);
    } catch {
      toast.error("Could not update.");
    }
  };

  return (
    <div className="space-y-8 pt-6 border-t border-[#0a0a0a]/10">
      {/* Mood boards */}
      {moodBoards.length > 0 && (
        <section data-testid="crm-moodboards">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon size={14} className="text-[#0a0a0a]/60" />
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#0a0a0a]/60">
              Mood boards ({moodBoards.length})
            </p>
          </div>
          <div className="space-y-4">
            {moodBoards.map((mb) => (
              <div key={mb.id} className="border border-[#0a0a0a]/10 rounded-sm p-4 bg-[#fafafa]">
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0a0a0a]/50">
                      Submitted {formatDate(mb.submitted_at)}
                    </p>
                    {mb.shoot_style && (
                      <p className="text-sm font-semibold text-[#0a0a0a] mt-1">{mb.shoot_style}</p>
                    )}
                  </div>
                  <StatusPill status={mb.status || "pending"} />
                </div>
                {mb.images?.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
                    {mb.images.map((src, i) => (
                      <button
                        key={`${mb.id}-${i}`}
                        type="button"
                        onClick={() => onImageClick?.(src, mb.images, i)}
                        className="aspect-[3/4] overflow-hidden bg-[#0a0a0a]/5"
                      >
                        <img src={src} alt={`mb ${i}`} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                      </button>
                    ))}
                  </div>
                )}
                {mb.preferred_dates?.length > 0 && (
                  <p className="text-xs text-[#0a0a0a]/70 mb-1">
                    <span className="uppercase tracking-[0.2em] font-bold text-[#0a0a0a]/40 mr-2">Dates</span>
                    {mb.preferred_dates.join(", ")}
                  </p>
                )}
                {mb.notes && (
                  <p className="text-sm text-[#0a0a0a]/70 mb-2">{mb.notes}</p>
                )}
                <div className="flex gap-2 pt-2">
                  <button
                    data-testid={`mb-approve-${mb.id}`}
                    onClick={() => decideMoodBoard(mb.id, "approved")}
                    disabled={mb.status === "approved"}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 border border-emerald-600 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40 rounded-sm"
                  >
                    <Check size={12} /> Approve
                  </button>
                  <button
                    data-testid={`mb-decline-${mb.id}`}
                    onClick={() => decideMoodBoard(mb.id, "declined")}
                    disabled={mb.status === "declined"}
                    className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 border border-[#0a0a0a]/25 text-[#0a0a0a]/60 hover:bg-[#0a0a0a]/5 disabled:opacity-40 rounded-sm"
                  >
                    <X size={12} /> Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Notes */}
      <section data-testid="crm-notes">
        <div className="flex items-center gap-2 mb-4">
          <StickyNote size={14} className="text-[#0a0a0a]/60" />
          <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#0a0a0a]/60">
            Notes ({notes.length})
          </p>
        </div>
        <div className="space-y-2 mb-3">
          {notes.length === 0 && (
            <p className="text-xs text-[#0a0a0a]/40 italic">No notes yet.</p>
          )}
          {notes.map((n) => (
            <div key={n.id} className="border border-[#0a0a0a]/10 rounded-sm p-3 bg-white flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#0a0a0a]/85 whitespace-pre-wrap break-words">{n.text}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#0a0a0a]/40 mt-1">
                  {n.author} · {formatDate(n.at)}
                </p>
              </div>
              <button
                data-testid={`note-delete-${n.id}`}
                onClick={() => removeNote(n.id)}
                className="text-[#0a0a0a]/30 hover:text-[#000000] transition-colors p-1"
                aria-label="Delete note"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <textarea
            data-testid="crm-note-input"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            rows={2}
            placeholder="Log a note — call summary, agent feedback, casting outcome…"
            className="flex-1 border border-[#0a0a0a]/15 rounded-sm p-3 text-sm focus:outline-none focus:border-[#0a0a0a] resize-none"
          />
          <button
            data-testid="crm-note-submit"
            onClick={submitNote}
            disabled={busy || !noteDraft.trim()}
            className="self-end inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2 bg-[#0a0a0a] text-white rounded-sm hover:bg-[#1f1f1f] disabled:opacity-50"
          >
            <Plus size={12} /> Add
          </button>
        </div>
      </section>

      {/* Meetings */}
      <section data-testid="crm-meetings">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <CalendarPlus size={14} className="text-[#0a0a0a]/60" />
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#0a0a0a]/60">
              Meetings ({meetings.length})
            </p>
          </div>
          <button
            data-testid="crm-meeting-toggle"
            onClick={() => setShowMeetingForm((s) => !s)}
            className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0a0a0a]/60 hover:text-[#0a0a0a]"
          >
            {showMeetingForm ? "Cancel" : "+ Log meeting"}
          </button>
        </div>
        {showMeetingForm && (
          <MeetingForm busy={busy} onCancel={() => setShowMeetingForm(false)} onSubmit={submitMeeting} />
        )}
        <div className="space-y-2 mt-3">
          {meetings.length === 0 && !showMeetingForm && (
            <p className="text-xs text-[#0a0a0a]/40 italic">No meetings logged yet.</p>
          )}
          {meetings.map((m) => (
            <div key={m.id} className="border border-[#0a0a0a]/10 rounded-sm p-3 bg-white flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#0a0a0a]">{m.title}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#0a0a0a]/50 mt-0.5">
                  {formatDate(m.at)}{m.medium ? ` · ${m.medium}` : ""}{m.attendees ? ` · ${m.attendees}` : ""} · logged by {m.logged_by}
                </p>
                {m.notes && (
                  <p className="text-sm text-[#0a0a0a]/70 mt-2 whitespace-pre-wrap break-words">{m.notes}</p>
                )}
              </div>
              <button
                data-testid={`meeting-delete-${m.id}`}
                onClick={() => removeMeeting(m.id)}
                className="text-[#0a0a0a]/30 hover:text-[#000000] transition-colors p-1"
                aria-label="Delete meeting"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

const StatusPill = ({ status }) => {
  const cls =
    status === "approved"
      ? "bg-emerald-600 text-white"
      : status === "declined"
      ? "bg-neutral-400 text-white"
      : "bg-amber-500 text-black";
  return (
    <span className={`inline-flex items-center rounded-sm px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-bold ${cls}`}>
      {status}
    </span>
  );
};

const MeetingForm = ({ busy, onSubmit, onCancel }) => {
  const nowLocal = () => {
    const d = new Date();
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d.toISOString().slice(0, 16);
  };
  const [title, setTitle] = useState("");
  const [at, setAt] = useState(nowLocal());
  const [medium, setMedium] = useState("call");
  const [attendees, setAttendees] = useState("");
  const [notes, setNotes] = useState("");

  const commit = () => {
    if (!title.trim()) {
      toast.error("Add a title for the meeting.");
      return;
    }
    onSubmit({
      title: title.trim(),
      at: new Date(at).toISOString(),
      medium,
      attendees: attendees || null,
      notes: notes || null,
    });
  };

  return (
    <div className="border border-[#0a0a0a]/15 rounded-sm p-4 bg-[#fafafa] space-y-3" data-testid="crm-meeting-form">
      <input
        data-testid="meeting-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title — e.g. Casting call — Rimzim Dadu SS26"
        className="w-full border border-[#0a0a0a]/15 rounded-sm p-2 text-sm focus:outline-none focus:border-[#0a0a0a]"
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          data-testid="meeting-date"
          type="datetime-local"
          value={at}
          onChange={(e) => setAt(e.target.value)}
          className="w-full border border-[#0a0a0a]/15 rounded-sm p-2 text-sm focus:outline-none focus:border-[#0a0a0a]"
        />
        <select
          data-testid="meeting-medium"
          value={medium}
          onChange={(e) => setMedium(e.target.value)}
          className="w-full border border-[#0a0a0a]/15 rounded-sm p-2 text-sm focus:outline-none focus:border-[#0a0a0a] bg-white"
        >
          <option value="call">Call</option>
          <option value="video">Video</option>
          <option value="in-person">In person</option>
          <option value="email">Email</option>
          <option value="other">Other</option>
        </select>
      </div>
      <input
        data-testid="meeting-attendees"
        value={attendees}
        onChange={(e) => setAttendees(e.target.value)}
        placeholder="Attendees (comma separated)"
        className="w-full border border-[#0a0a0a]/15 rounded-sm p-2 text-sm focus:outline-none focus:border-[#0a0a0a]"
      />
      <textarea
        data-testid="meeting-notes"
        rows={3}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes"
        className="w-full border border-[#0a0a0a]/15 rounded-sm p-2 text-sm focus:outline-none focus:border-[#0a0a0a] resize-none"
      />
      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0a0a0a]/60 hover:text-[#0a0a0a] px-3 py-2"
        >
          Cancel
        </button>
        <button
          data-testid="meeting-save"
          onClick={commit}
          disabled={busy}
          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold px-4 py-2 bg-[#0a0a0a] text-white rounded-sm hover:bg-[#1f1f1f] disabled:opacity-50"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />} Save
        </button>
      </div>
    </div>
  );
};
